import React from 'react';
import { connect } from 'react-redux';
import { compose, mapProps } from 'recompose';
import { isDefined } from '@peloton/types/isDefined';
import { useReduxState } from '@engage/redux/hooks';
import { useModalLabel } from '@members/copy';
import type { CopyIDs } from '@members/copy';
import type { ModalLabel } from '@members/copy/models.generated/ModalLabel';
import { useMembersRouter as useRouter } from '@members/localization/useMembersRouter';
import type { ModalProps } from './Modal';
import { Modal } from './Modal';
import type { ModalName, ModalSelectorState } from './redux';
import { getActiveModalName, getActiveModalOpenedVia } from './redux';
import { toQueryParamsWithoutModal } from './sagas';

const getModalContent = (modalNames: ModalMap, modalName?: ModalName) =>
  isDefined(modalName) && modalNames.hasOwnProperty(modalName)
    ? modalNames[modalName]
    : undefined;

const handleClosing = (props: DispatchProps & Partial<ModalContent>) => {
  props.closeModal();
  if (isDefined(props.onCloseModal)) {
    props.onCloseModal();
  }
};

export const ConnectedModal: React.FC<React.PropsWithChildren<OwnProps>> = props => {
  const router = useRouter();

  // Determine the active modal name from two possible sources:
  //
  // 1. Redux state — set by programmatic openModal() dispatches (e.g. Find Members
  //    button, Facebook connect, Followers list) or by connected-react-router's
  //    LOCATION_CHANGE on initial page load.
  //
  // 2. URL query param — router.query.modal (e.g. ?modal=tags&view=browseTags).
  //
  // The Redux state tracks HOW the modal was opened via `openedVia`:
  //   - 'action': opened programmatically via openModal() → trust Redux
  //   - 'url': opened by LOCATION_CHANGE reading ?modal= from URL → trust the URL
  //     because connected-react-router only fires LOCATION_CHANGE once on initial
  //     page load; subsequent Next.js navigations bypass the history library, so
  //     LOCATION_CHANGE never fires again to clear the stale Redux state.
  //   - 'none': no modal open
  const reduxModalName = useReduxState((state: ModalSelectorState) =>
    getActiveModalName(state),
  );
  const openedVia = useReduxState((state: ModalSelectorState) =>
    getActiveModalOpenedVia(state),
  );
  const modalName =
    openedVia === 'action' && isDefined(reduxModalName)
      ? reduxModalName
      : (router.query.modal as string | undefined);

  const closeModalHandler = (modalIdentifiers: string[]) =>
    router.push(
      {
        query: toQueryParamsWithoutModal(router.query, modalIdentifiers),
      },
      undefined,
      { scroll: false },
    );
  const modalContent = getModalContent(props.modalNames, modalName);

  if (isDefined(modalContent)) {
    const { component, ...modalProps } = modalContent;
    return (
      <Modal
        isOpen={true}
        children={component}
        contentLabel={modalProps.ariaLabelId}
        closeHandler={() =>
          handleClosing({
            closeModal: () => closeModalHandler(modalProps.modelIdentifiers!),
            onCloseModal: modalProps.onCloseModal,
          })
        }
        {...modalProps}
        {...props}
      />
    );
  }

  return <Modal isOpen={false} contentLabel="" />;
};

/**
 * @deprecated in favor of hook based ConnectedModal
 */

export const LegacyConnectedModal = compose<ModalProps, OwnProps & Partial<ModalProps>>(
  connect((state: ModalSelectorState) => ({
    modalName: getActiveModalName(state),
    openedVia: getActiveModalOpenedVia(state),
  })),
  mapProps((props: StateProps & DispatchProps & OwnProps) => {
    const router = useRouter();
    // Same logic as ConnectedModal: trust Redux for programmatic opens, URL for the rest
    const modalName =
      props.openedVia === 'action' && isDefined(props.modalName)
        ? props.modalName
        : (router.query.modal as string | undefined);
    const modalContent = getModalContent(props.modalNames, modalName);

    const closeModalHandler = (modalIdentifiers: string[]) =>
      router.push(
        {
          query: toQueryParamsWithoutModal(router.query, modalIdentifiers),
        },
        undefined,
        { scroll: false },
      );

    if (isDefined(modalContent)) {
      const { component, ...modalProps } = modalContent;
      return {
        ...props,
        ...modalProps,
        children: component,
        contentLabel: useModalLabel(modalContent.ariaLabelId),
        closeHandler: () =>
          handleClosing({
            closeModal: () => closeModalHandler(modalProps.modelIdentifiers!),
            onCloseModal: modalProps.onCloseModal,
          }),
        isOpen: true,
      };
    } else {
      return { isOpen: false };
    }
  }),
)(Modal);

type ModalContent = {
  component: React.ReactElement<any>;
  modelIdentifiers?: string[];
  ariaLabelId: CopyIDs<typeof ModalLabel>;
  onCloseModal?(): void;
} & Partial<ModalProps>;
export type ModalMap = Record<ModalName, ModalContent>;
type OwnProps = { modalNames: ModalMap };
type StateProps = { modalName?: ModalName; isOpen?: boolean; openedVia?: string };
type DispatchProps = { closeModal(modalIdentifiers?: string[]): void };
